<script setup lang="ts">
import type { ProRouteRaw } from '@inn/components';
import { useLayoutState, XomProActionItem, XomProActions, XomProAvatar } from '@inn/components';
import { computed, h, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IS_DEV } from '@inn/const';
import {
  BellOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  LockOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  SafetyOutlined,
  SecurityScanOutlined,
  SwapOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons-vue';
import { usePermitStore } from '@/storage/permits';
import { useProfileStore } from '@/storage/profile';
import { signout } from '@/utils/signin';
import { useNotifyStore } from '@/storage/notify';

defineOptions({ name: 'XomHeadActions' });

const router = useRouter();
const profile = useProfileStore();
const permits = usePermitStore();
const notify = useNotifyStore();

const { rootMenus, effective } = useLayoutState();
const warnPwdChange = new Set(['DEFAULT_RESET', 'OUTDATED_RESET']);
const forcePwdChange = new Set(['DEFAULT_FORCE_RESET', 'OUTDATED_FORCE_RESET']);
const isWarnPwdChange = computed(() => warnPwdChange.has(profile.passwordResetType));
const isForcePwdChange = computed(() => forcePwdChange.has(profile.passwordResetType));
const isFirstTime = computed(() =>
  ['DEFAULT_RESET', 'DEFAULT_FORCE_RESET'].includes(profile.passwordResetType),
);

const systemFormOpen = ref(false);
const notifyVisible = ref(false);
const siteSwitcherOpen = ref(false);
const pwdResetModalOpen = ref(isWarnPwdChange.value || isForcePwdChange.value);

const notifyShaking = ref(false);

function playNotifySound() {
  try {
    const audio = new Audio('/notify/notify.mp3');
    audio.volume = 1;
    audio.play().catch(() => {
      /* 自动播放被浏览器策略阻止时忽略 */
    });
  } catch {
    /* 忽略 */
  }
}

function triggerNotifyShake() {
  notifyShaking.value = true;
  window.setTimeout(() => {
    notifyShaking.value = false;
  }, 800);
}

watch(
  () => notify.unreadNum,
  (newVal, oldVal) => {
    const prev = typeof oldVal === 'number' ? oldVal : 0;
    if (typeof newVal === 'number' && newVal > prev) {
      triggerNotifyShake();
      if (notify.soundEnabled) {
        playNotifySound();
      }
    }
  },
);

notify.getEventType();
function showNotify() {
  // if (notify.soundEnabled) {
  //   playNotifySound();
  // }
  notifyVisible.value = !notifyVisible.value;
  notify.getUnreadNotify();
}

const preferenceMenus = computed(() => {
  return effective.layout === 'top'
    ? rootMenus.value.find((m_) => m_.path.startsWith('/preference'))?.children || []
    : [];
});

const profileMenus = computed<Partial<ProRouteRaw>[]>(() => {
  function getAvatar(userType: (typeof profile)['type']) {
    const icons = { SYSTEM: RobotOutlined, SUPER: GlobalOutlined, NORMAL: UserOutlined };
    const classes = { SYSTEM: 'text-warning', SUPER: 'text-success', NORMAL: '' };
    return () => h(icons[userType], { class: classes[userType] });
  }
  return [
    {
      meta: { icon: getAvatar(profile.type), title: profile.nickname },
    },
    {
      meta: { icon: SafetyOutlined, title: '账号安全', dividerBefore: true },
      onClick: () => router.push('/member/security'),
    },
    {
      meta: { icon: LockOutlined, title: '修改密码' },
      onClick: handlePwdReset,
    },
    {
      meta: { icon: SyncOutlined, title: '刷新个人信息' },
      onClick: refresh,
    },
    {
      meta: { icon: SecurityScanOutlined, title: '重载权限' },
      onClick: reauth,
    },
    ...(profile.site?.nickName && siteOptions.value.length
      ? [
          {
            meta: { icon: EnvironmentOutlined, title: profile.site?.name },
            onClick: () => (siteSwitcherOpen.value = true),
          },
        ]
      : []),
    {
      meta: { icon: QuestionCircleOutlined, title: '帮助与反馈' },
      onClick: toHelp,
    },
    {
      meta: { icon: LogoutOutlined, title: '退出登录', dividerBefore: true },
      onClick: signout,
    },
  ];
});

function toHelp() {
  router.push('/member/help');
}

const siteOptions = computed(
  () =>
    permits.sites?.reduce<{ label: string; value: string }[]>((prev, s: BaseRelates) => {
      return s.id === profile.site?.id ? prev : [...prev, { label: s.name, value: s.id }];
    }, []) ?? [],
);

function handlePwdReset() {
  pwdResetModalOpen.value = !pwdResetModalOpen.value;
}

async function refresh() {
  await profile.reload();
}

async function reauth() {
  await permits.reload();
  router.go(0);
}

function isActiveClass(url: string) {
  const isActive = useRoute().path.startsWith(url);
  return isActive ? 'is-active' : '';
}
</script>

<template>
  <xom-pro-actions>
    <!-- “本地开发注册”入口 -->
    <xom-pro-action-item
      v-if="IS_DEV"
      title="注册本地开发环境子系统"
      @click="systemFormOpen = !systemFormOpen"
    >
      <template #icon>
        <plus-circle-outlined />
      </template>
    </xom-pro-action-item>

    <!-- 消息通知 -->
    <xom-pro-action-item @click="showNotify">
      <template #icon>
        <div class="notify-action-icon" :class="{ 'notify-action-icon--shake': notifyShaking }">
          <bell-outlined :class="isActiveClass('/member/message')" />
          <a-badge :count="notify.unreadNum" :offset="[5, 0]" :dot="true">
            <div class="anticon" />
          </a-badge>
          <a-popover v-model:open="notifyVisible" trigger="click" overlay-class-name="xom-popover">
            <div class="blank-element" />
            <template #content>
              <div class="notify-popover-wrapper">
                <xom-notify
                  v-model:count="notify.unreadNum"
                  @close="notifyVisible = !notifyVisible"
                />
              </div>
            </template>
          </a-popover>
        </div>
      </template>
    </xom-pro-action-item>

    <!-- “系统配置”导航菜单 -->
    <xom-pro-action-item
      v-if="permits.systems.some((s_) => s_.activeRule?.startsWith('/preference'))"
      href="/preference"
      :dropdown-menus="preferenceMenus"
    >
      <template #icon><setting-outlined /></template>
    </xom-pro-action-item>

    <!-- “站点切换”入口 -->
    <!-- 有简称 -->
    <!--    <xom-pro-action-item v-if="profile.site?.nickName" :title="profile.site?.name"> -->
    <!--      <template #icon /> -->
    <!--    </xom-pro-action-item> -->
    <!-- “站点切换”入口 -->
    <xom-pro-action-item
      :title="profile.site?.name"
      :is-hide-icon="true"
      @click="siteSwitcherOpen = true"
    >
      <template #default>
        <div style="display: flex; align-items: center; font-size: 12px">
          <span v-if="profile.site?.nickName" class="xom-pro-actions_nickname">
            <span>
              {{ profile.site?.nickName }}
            </span>
          </span>
          <swap-outlined
            v-if="profile.site?.nickName && siteOptions.length"
            class="site-mark-point"
          />
          <environment-outlined v-else class="site-mark-point" />
        </div>
      </template>
    </xom-pro-action-item>

    <!-- 用户头像 -->
    <xom-pro-action-item
      :tooltip="profile.authed ? undefined : '未登录'"
      :disabled="!profile.authed"
      :dropdown-menus="profileMenus"
      dropdown-trigger="click"
      style="margin-left: 12px"
    >
      <template #icon>
        <xom-pro-avatar
          v-if="profile.authed"
          :src="profile.avatar"
          :name="profile.nickname"
          :gender="profile.gender?.toLowerCase() as any"
        />
        <login-outlined v-else />
      </template>
    </xom-pro-action-item>

    <xom-system-form-modal v-if="IS_DEV" v-model:open="systemFormOpen" type="develop" />

    <xom-site-switch-modal v-model:open="siteSwitcherOpen" />

    <xom-pwd-reset
      v-model:visible="pwdResetModalOpen"
      :is-show-tip="isWarnPwdChange || isForcePwdChange"
      :is-hide-close="isForcePwdChange"
      :is-first-time="isFirstTime"
      @ok="signout"
    />
  </xom-pro-actions>
</template>

<style lang="scss">
@use '@inn/assets/scss/vars';

.xom-pro-actions_nickname {
  display: inline-block;
  max-width: 6em;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  white-space: nowrap;
}

.site-mark-point {
  font-size: 14px;
}

.xom-popover {
  .#{vars.$ant-prefix}-popover-inner {
    padding: 0;
  }
}

.notify-icon {
  color: var(--xom-color-info);
}

.blank-element {
  position: relative;
}

.is-active {
  color: var(--xom-color-primary);
}

.notify-action-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &--shake {
    animation: notify-shake 0.8s ease-in-out;
  }
}

@keyframes notify-shake {
  0%,
  100% {
    transform: translateX(0);
  }

  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-3px);
  }

  20%,
  40%,
  60%,
  80% {
    transform: translateX(3px);
  }
}

.notify-popover-wrapper {
  width: 300px;
}

.notify-popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid var(--xom-color-border-secondary);
}

.notify-popover-title {
  font-size: 14px;
  font-weight: 600;
}

.notify-popover-sound {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 16px;
  color: var(--xom-color-text-secondary);
  cursor: pointer;
  border-radius: 4px;
  transition:
    background-color 0.2s ease-in-out,
    color 0.2s ease-in-out;

  &:hover {
    background-color: var(--xom-color-fill-tertiary);
  }

  &--on {
    color: var(--xom-color-primary);
  }
}
</style>
